import { getPrimaryTag } from './useTagActions';

/**
 * useNestedSprintActions Hook
 * 
 * Handles nested sprint operations: create, remove, check nesting capability
 */

export const useNestedSprintActions = (localSprint, setLocalSprint, allSprints = [], allCategories = []) => {

  /**
   * Create a nested sprint for a task
   * Returns the new sprint data so caller can add it to the sprints list
   * 
   * @param {string} taskId - The task to create a nested sprint for
   * @param {boolean} forceCreate - If true, creates new sprint even if task has nestedSprintId
   */
  const handleCreateNestedSprint = (taskId, forceCreate = false) => {
    const tasks = localSprint?.tasks || [];
    const task = tasks.find(t => t.id === taskId);
    if (!task) {
      return null;
    }
    
    // Check if task already has a nested sprint
    if (task.nestedSprintId && !forceCreate) {
      // Check if the referenced sprint actually exists
      const existingSprint = allSprints.find(s => s.id === task.nestedSprintId);
      if (existingSprint) {
        return task.nestedSprintId;
      }
      // Sprint doesn't exist - it was deleted. Clear the orphaned reference and create new.
      
      // Log to audit
      if (window.pletAudit) {
        window.pletAudit.log('ORPHANED_NESTED_SPRINT_CLEARED', {
          taskId,
          taskName: task.name,
          orphanedNestedSprintId: task.nestedSprintId,
          parentSprintId: localSprint?.id,
        }, localSprint?.id);
      }
    }
    
    // Create new nested sprint
    const newSprintId = `nested-sprint-${Date.now()}`;
    
    // Log to audit
    if (window.pletAudit) {
      window.pletAudit.log('NESTED_SPRINT_CREATED', {
        newSprintId,
        taskId,
        taskName: task.name,
        parentSprintId: localSprint?.id,
        parentSprintName: localSprint?.name,
      }, localSprint?.id);
    }
    
    // Update the task with the nested sprint ID
    setLocalSprint(prev => {
      if (!prev) return prev;
      const prevTasks = prev.tasks || [];
      return {
        ...prev,
        tasks: prevTasks.map(t => 
          t.id === taskId ? { ...t, nestedSprintId: newSprintId } : t
        ),
      };
    });
    
    // Return the new sprint data (caller will add it to the sprints list)
    // Name defaults to the task name (truncated if over 15 chars)
    const sprintName = task.name && task.name.length > 15 
      ? task.name.slice(0, 35) + '...' 
      : (task.name || 'Untitled');
    
    // Transfer the task's icon to the new nested campaign
    // From now on, the icon lives on the campaign, not the task
    // 
    // If the icon has color: 'inherit', resolve it to the actual primary tag color
    // This ensures the campaign icon displays correctly even outside the task context
    let transferredIcon = task.icon || null;
    if (transferredIcon?.color === 'inherit') {
      // Get the task's primary tag color
      const primaryTagName = getPrimaryTag(task);
      const primaryTag = primaryTagName ? allCategories.find(c => c.name === primaryTagName) : null;
      const resolvedColor = primaryTag?.color || '#9ca3af';
      
      // Store the resolved color on the icon, but also keep a flag so we know it was 'inherit'
      // This allows us to re-resolve if the tag color changes
      transferredIcon = { 
        ...transferredIcon, 
        color: resolvedColor,
        _inheritedFromTag: primaryTagName, // Track which tag it inherited from
      };
    }
    
    const newSprint = {
      id: newSprintId,
      name: sprintName,
      icon: transferredIcon,
      ownerId: localSprint.ownerId,
      ownerName: localSprint.ownerName,
      status: 'active',
      progress: 0,
      createdAt: new Date().toISOString(),
      parentTaskId: taskId,
      parentSprintId: localSprint.id,
      tasks: [],
      // PUT /sprints requires these arrays — omit them and later autosaves 400.
      categories: [],
      connections: [],
    };
    
    // Clear the icon from the task since it now lives on the nested campaign
    // (The task will derive its icon from the nested campaign)
    if (task.icon) {
      setLocalSprint(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          tasks: prev.tasks.map(t => 
            t.id === taskId ? { ...t, icon: null } : t
          ),
        };
      });
    }
    
    return newSprint;
  };

  /**
   * Remove a nested sprint from a task (unlink only — the task/mission is kept)
   * Returns the nested sprint ID so caller can remove it from sprints list
   * Note: Confirmation should be handled by the UI before calling this
   */
  const handleRemoveNestedSprint = (taskId) => {
    const tasks = localSprint?.tasks || [];
    const task = tasks.find(t => t.id === taskId);
    const nestedSprintId = task?.nestedSprintId;
    
    if (!nestedSprintId) return null;
    
    // Log to audit
    if (window.pletAudit) {
      window.pletAudit.log('NESTED_SPRINT_REMOVED', {
        nestedSprintId,
        taskId,
        taskName: task?.name,
        parentSprintId: localSprint?.id,
        parentSprintName: localSprint?.name,
      }, localSprint?.id);
    }
    
    // When the nested campaign was created, the task's icon was transferred to it
    // (the task then derives its icon from the campaign). Now that the task becomes
    // a plain mission again, copy the icon back so it doesn't silently disappear.
    let restoredIcon = null;
    if (!task.icon) {
      const nestedSprint = allSprints.find(s => s.id === nestedSprintId);
      if (nestedSprint?.icon) {
        const { _inheritedFromTag, ...icon } = nestedSprint.icon;
        // If the icon originally inherited its color from the task's primary tag,
        // restore the 'inherit' marker so it keeps tracking the tag color.
        restoredIcon = _inheritedFromTag ? { ...icon, color: 'inherit' } : icon;
      }
    }
    
    setLocalSprint(prev => {
      if (!prev) return prev;
      const prevTasks = prev.tasks || [];
      return {
        ...prev,
        tasks: prevTasks.map(t => 
          t.id === taskId
            ? { ...t, nestedSprintId: null, ...(restoredIcon ? { icon: restoredIcon } : {}) }
            : t
        ),
      };
    });
    
    return nestedSprintId;
  };

  /**
   * Check if this sprint can have nested sprints
   * (All sprints can - no nesting limit like Notion pages)
   */
  const canHaveNestedSprints = () => {
    return true;
  };

  return {
    handleCreateNestedSprint,
    handleRemoveNestedSprint,
    canHaveNestedSprints,
  };
};

export default useNestedSprintActions;

